import type { ReactNode } from "react";
import {
  Badge,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import type {
  InstructorPayoutRateItemDTO,
  InstructorPayoutWorkKind,
} from "@/modules/master/domain/contracts";

const WORK_KIND_META: Record<
  InstructorPayoutWorkKind,
  { short: string; full: string; tone: string }
> = {
  general_class: {
    short: "ທົ່ວໄປ",
    full: "ຄລາສທົ່ວໄປ",
    tone: "border-blue-500/30 bg-blue-500/10 text-blue-900 dark:text-blue-200",
  },
  private_lesson: {
    short: "ສ່ວນຕົວ",
    full: "ສ່ວນຕົວ",
    tone: "border-violet-500/30 bg-violet-500/10 text-violet-900 dark:text-violet-200",
  },
  partner: {
    short: "Partner",
    full: "Partner",
    tone: "border-dashed border-muted-foreground/40 bg-muted/40 text-muted-foreground",
  },
  room_rental_assist: {
    short: "ເຊົ່າຫ້ອງ",
    full: "ຊ່ວຍສອນເຊົ່າຫ້ອງ",
    tone: "border-orange-500/30 bg-orange-500/10 text-orange-900 dark:text-orange-200",
  },
  general_class_assist: {
    short: "TA",
    full: "ຊ່ວຍສອນຄລາດສ໌ (TA)",
    tone: "border-teal-500/30 bg-teal-500/10 text-teal-900 dark:text-teal-200",
  },
};

function locationSuffix(
  workKind: InstructorPayoutWorkKind,
  locationKind: string,
): string {
  if (workKind === "partner" || workKind === "room_rental_assist") return "";
  if (locationKind === "off_site") return " · ນອກ";
  if (locationKind === "off_site_near") return " · ໃກ້";
  if (locationKind === "off_site_far") return " · ໄກ";
  return " · Studio";
}

function locationLabelFull(
  workKind: InstructorPayoutWorkKind,
  locationKind: string,
): string {
  if (workKind === "partner" || workKind === "room_rental_assist") {
    return "—";
  }
  if (locationKind === "off_site") return "ນອກສະຖານທີ່";
  if (locationKind === "off_site_near") return "ນອກສະຖານທີ່ໃກ້";
  if (locationKind === "off_site_far") return "ນອກສະຖານທີ່ໄກ";
  return "ໃນສະຖານທີ່";
}

function payoutBasisLabel(basis: "per_hour" | "per_session"): string {
  return basis === "per_session" ? "ຕໍ່ຄັ້ງ" : "ຕໍ່ຊົ່ວໂມງ";
}

export function EmploymentTypeBadge({
  type,
  className,
}: {
  type: "full_time" | "freelance";
  className?: string;
}) {
  const isFullTime = type === "full_time";
  return (
    <Badge
      variant={isFullTime ? "default" : "outline"}
      className={cn(
        "text-[11px] font-medium",
        isFullTime &&
          "border-indigo-600/20 bg-indigo-600 text-white hover:bg-indigo-600",
        !isFullTime &&
          "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100",
        className,
      )}
    >
      {isFullTime ? "ປະຈຳ" : "ຟຣີແລນ"}
    </Badge>
  );
}

export function InstructorStatusBadge({
  status,
  className,
}: {
  status: "active" | "inactive";
  className?: string;
}) {
  const isActive = status === "active";
  return (
    <Badge
      variant={isActive ? "default" : "secondary"}
      className={cn(
        "text-[11px]",
        isActive &&
          "border-emerald-600/20 bg-emerald-600 text-white hover:bg-emerald-600",
        !isActive && "bg-muted text-muted-foreground",
        className,
      )}
    >
      {isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
    </Badge>
  );
}

export function MonthlyFreeQuotaBadge({
  quota,
  compact,
  className,
}: {
  quota: number;
  compact?: boolean;
  className?: string;
}) {
  if (quota <= 0) {
    return (
      <span className={cn("text-muted-foreground text-xs", className)}>—</span>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 border-emerald-500/40 bg-emerald-500/10 font-medium text-[11px] text-emerald-800 tabular-nums dark:text-emerald-200",
        className,
      )}
    >
      {compact ? `${quota} ຟຣີ` : `${quota} session ຟຣີ/ເດືອນ`}
    </Badge>
  );
}

export function MonthlyFreeQuotaMeter({
  quota,
  used,
  billable,
  className,
}: {
  quota: number;
  used: number;
  billable?: number;
  className?: string;
}) {
  if (quota <= 0) return null;
  const pct = Math.min(100, Math.round((used / quota) * 100));
  const exhausted = used >= quota;

  return (
    <div
      className={cn(
        "rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3",
        className,
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-medium text-emerald-900 text-sm dark:text-emerald-100">
          Retainer quota
        </span>
        <MonthlyFreeQuotaBadge quota={quota} compact />
      </div>
      <div className="mb-2 h-2 overflow-hidden rounded-full bg-emerald-950/10 dark:bg-emerald-950/30">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            exhausted ? "bg-amber-500" : "bg-emerald-500",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground text-xs tabular-nums">
        <span>
          ໃຊ້ແລ້ວ{" "}
          <strong className="text-foreground">{used.toLocaleString("lo-LA")}</strong>
          /{quota.toLocaleString("lo-LA")}
        </span>
        {billable != null && billable > 0 ? (
          <span>
            ຈ່າຍເພີ່ມ{" "}
            <strong className="text-foreground">
              {billable.toLocaleString("lo-LA")}
            </strong>{" "}
            ຄັ້ງ
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function PayoutRateChip({
  rate,
  className,
}: {
  rate: InstructorPayoutRateItemDTO;
  className?: string;
}) {
  const meta = WORK_KIND_META[rate.workKind];
  const unit = rate.payoutBasis === "per_session" ? "ຄັ້ງ" : "ຊມ.";
  return (
    <Badge
      variant="outline"
      className={cn(
        "max-w-full truncate font-normal text-[10px] tabular-nums",
        meta.tone,
        className,
      )}
      title={`${meta.full}${locationSuffix(rate.workKind, rate.locationKind ?? "studio")}: ${rate.amountKip.toLocaleString("lo-LA")} ₭/${unit}`}
    >
      {meta.short}
      {locationSuffix(rate.workKind, rate.locationKind ?? "studio")}{" "}
      {rate.amountKip.toLocaleString("lo-LA")}/{unit}
    </Badge>
  );
}

export function PayoutRateChips({
  rates,
  className,
  maxVisible,
}: {
  rates: InstructorPayoutRateItemDTO[];
  className?: string;
  maxVisible?: number;
}) {
  const active = rates.filter((r) => r.amountKip > 0);
  if (active.length === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const visible =
    maxVisible != null ? active.slice(0, maxVisible) : active;
  const hidden = active.length - visible.length;
  return (
    <div className={cn("flex max-w-xs flex-wrap gap-1", className)}>
      {visible.map((r) => (
        <PayoutRateChip
          key={`${r.workKind}:${r.locationKind ?? "studio"}`}
          rate={r}
        />
      ))}
      {hidden > 0 ? (
        <Badge variant="outline" className="text-[10px] tabular-nums">
          +{hidden}
        </Badge>
      ) : null}
    </div>
  );
}

/** สรุปใน cell ก่อน expand */
export function PayoutRatesCellSummary({
  rates,
  className,
}: {
  rates: InstructorPayoutRateItemDTO[];
  className?: string;
}) {
  const active = rates.filter((r) => r.amountKip > 0);
  if (active.length === 0) {
    return (
      <span className={cn("text-muted-foreground text-xs", className)}>
        ຍັງບໍ່ຕັ້ງອັດຕາ
      </span>
    );
  }
  return (
    <div className={cn("space-y-1.5 py-0.5", className)}>
      <div className="flex items-baseline gap-1.5 tabular-nums">
        <span className="font-semibold text-sm">{active.length}</span>
        <span className="text-muted-foreground text-xs">
          ອັດຕາ · ກົດ ▶ ເບິ່ງຕາຕະລາງ
        </span>
      </div>
      <PayoutRateChips rates={rates} maxVisible={2} />
    </div>
  );
}

/** ตาราง expand แสดงอัตราค่าจ้างครบทุก slot */
export function PayoutRatesExpandedTable({
  rates,
  className,
}: {
  rates: InstructorPayoutRateItemDTO[];
  className?: string;
}) {
  const sorted = [...rates].sort((a, b) => {
    const kindOrder = [
      "general_class",
      "private_lesson",
      "partner",
      "room_rental_assist",
      "general_class_assist",
    ] as const;
    const ai = kindOrder.indexOf(a.workKind);
    const bi = kindOrder.indexOf(b.workKind);
    if (ai !== bi) return ai - bi;
    return (a.locationKind ?? "studio").localeCompare(b.locationKind ?? "studio");
  });

  return (
    <div className={cn("overflow-x-auto rounded-lg border bg-background", className)}>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="min-w-[140px]">ປະເພດງານ</TableHead>
            <TableHead className="min-w-[120px]">ສະຖານທີ່</TableHead>
            <TableHead className="min-w-[100px]">ວິທີຄິດ</TableHead>
            <TableHead className="min-w-[120px] text-right">ຈຳນວນ (₭)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((rate) => {
            const meta = WORK_KIND_META[rate.workKind];
            const hasAmount = rate.amountKip > 0;
            return (
              <TableRow
                key={`${rate.workKind}:${rate.locationKind ?? "studio"}`}
                className={cn(!hasAmount && "text-muted-foreground/80")}
              >
                <TableCell className="font-medium text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        hasAmount ? "bg-emerald-500" : "bg-muted-foreground/30",
                      )}
                      aria-hidden
                    />
                    {meta.full}
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {locationLabelFull(rate.workKind, rate.locationKind ?? "studio")}
                </TableCell>
                <TableCell className="text-sm">
                  {payoutBasisLabel(rate.payoutBasis)}
                </TableCell>
                <TableCell className="text-right font-medium text-sm tabular-nums">
                  {hasAmount
                    ? `${rate.amountKip.toLocaleString("lo-LA")} ₭`
                    : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function PayoutRateCard({
  rate,
  className,
}: {
  rate: InstructorPayoutRateItemDTO;
  className?: string;
}) {
  const meta = WORK_KIND_META[rate.workKind];
  const unit = rate.payoutBasis === "per_session" ? "ຕໍ່ຄັ້ງ" : "ຕໍ່ຊົ່ວໂມງ";
  const hasAmount = rate.amountKip > 0;

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-3 shadow-sm",
        hasAmount ? meta.tone.split(" ").slice(0, 2).join(" ") : "border-dashed",
        className,
      )}
    >
      <p className="font-medium text-muted-foreground text-xs">
        {meta.full}
        {locationSuffix(rate.workKind, rate.locationKind ?? "studio")}
      </p>
      <p
        className={cn(
          "mt-1 font-semibold text-lg tabular-nums",
          !hasAmount && "text-muted-foreground",
        )}
      >
        {hasAmount ? `${rate.amountKip.toLocaleString("lo-LA")} ₭` : "—"}
      </p>
      <p className="mt-0.5 text-muted-foreground text-xs">{unit}</p>
    </div>
  );
}

export function PayoutRateGrid({
  rates,
  className,
}: {
  rates: InstructorPayoutRateItemDTO[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3",
        className,
      )}
    >
      {rates.map((r) => (
        <PayoutRateCard
          key={`${r.workKind}:${r.locationKind ?? "studio"}`}
          rate={r}
        />
      ))}
    </div>
  );
}

export function InstructorAvatar({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  return (
    <div
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-sm",
        className,
      )}
      aria-hidden
    >
      {initial}
    </div>
  );
}

export function InfoField({
  label,
  children,
  icon,
  className,
}: {
  label: string;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-muted/20 p-3.5",
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        {icon ? (
          <div className="mt-0.5 text-muted-foreground">{icon}</div>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-xs">{label}</p>
          <div className="mt-1 font-medium text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
}
